/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { forwardRef } from 'react';
import P1 from '@base/P1/P1';
import H6 from '@base/H6/H6';
import Divider from '@base/Divider/Divider';
import Download from '@icon/Download';
import pushRoute from '@utils/pushRoute';
import { PDF_DOWNLOAD } from '@constants/segment';
import segmentStorage from '@events/segmentStorage';
import { segment, analyticsApi } from '@actions/analytics';
import { Spacings } from '@styles/index';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getPlusSubscriptionInfo } from '@api/selectors/subscription';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';

const MenuItem = styled.li`
  display: flex;
  align-items: center;
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
  cursor: pointer;
`;

const StyledDivider = styled(Divider)`
  margin-bottom: ${Spacings.SPACING_4B};
`;

const Head = styled.li`
  display: flex;
  margin-top: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_3B};
`;

const DownloadPDF = styled(Download)`
  margin-right: ${Spacings.SPACING_3B};
  margin-left: ${Spacings.SPACING_4B};
`;

const PDFDownload = forwardRef(
  (
    {
      selectedLesson,
      course,
      goal,
      handleClose,
      isBatch = false,
      me = null,
      source = null,
      isBatchGroup = false
    },
    ref
  ) => {
    const { forPlus, forSubscription, isEnrolled } = course;
    const { uid: segGoalUID, name: goalName, language: goalLanguage } = goal;
    const goalUID = useSelectedGoalUID() || segGoalUID;
    const {
      subscription = {},
      expiredAt,
      timeRemaining
    } = useMyInfo(getPlusSubscriptionInfo(goalUID)) ?? {};

    const {
      name: lesssonName,
      uid: lessonUID,
      author: lessonAuthor
    } = selectedLesson;
    const eventData = {
      goal_uid: goalUID,
      name: goalName,
      language: goalLanguage,
      session_id: new Date().getTime(),
      subscription_duration: subscription.duration,
      subscription_end_date: expiredAt,
      is_subscription_active: timeRemaining > 0,
      lesson_uid: lessonUID,
      lesson_title: lesssonName,
      educator_uid: lessonAuthor?.uid,
      educator_username: lessonAuthor?.name,
      is_plus: forPlus,
      is_special: forPlus && !forSubscription,
      is_enrolled: isEnrolled,
      last_primary_source: segmentStorage.getItem('lps')
    };

    const { slides, type } = selectedLesson || {};
    const courseUID =
      selectedLesson?.courseUID ||
      selectedLesson?.programme?.course?.uid ||
      selectedLesson?.properties?.programme?.uid;
    const courseSlug =
      selectedLesson?.courseSlug ||
      selectedLesson?.programme?.course?.slug ||
      selectedLesson?.properties?.programme?.slug ||
      '-';
    const goToCoursePage = () => {
      pushRoute(
        `/${type === 'post' ? 'course' : 'test-series'}/[slug]/[courseUID]`,
        `/${
          type === 'post' ? 'course' : 'test-series'
        }/${courseSlug}/${courseUID}`
      );
      handleClose();
    };

    const openWith = () => {
      eventData.download_type = 'With annotations';
      if (source === 'learnerCoursePage') {
        segment.track(PDF_DOWNLOAD, eventData);
        analyticsApi(
          {
            event: 'PPT_DOWNLOAD',
            data: 'true',
            name: 'PPT_DOWNLOAD',
            isAnnotationOn: true,
            me
          },
          'downloadPDF'
        );
      }
      window.open(slides?.withAnnotations, '_blank');
      handleClose();
    };

    const openWithout = () => {
      eventData.download_type = 'Without annotations';
      if (source === 'learnerCoursePage') {
        segment.track(PDF_DOWNLOAD, eventData);
        analyticsApi(
          {
            event: 'PPT_DOWNLOAD',
            data: 'false',
            name: 'PPT_DOWNLOAD',
            isAnnotationOn: false,
            me
          },
          'downloadPDF'
        );
      }
      window.open(slides?.withoutAnnotations, '_blank');
      handleClose();
    };

    return (
      <>
        {slides && (
          <>
            <Head ref={ref}>
              <DownloadPDF size="20px" color="var(--color-text-primary)" />
              <H6 color="var(--color-text-primary)">Download PDF</H6>
            </Head>
            <StyledDivider />
          </>
        )}
        {slides?.withAnnotations ? (
          <MenuItem ref={ref}>
            <div
              onClick={openWith}
              className="slides"
              data-href={slides?.withAnnotations}
            >
              <P1 color="var(--color-text-primary)">With annotation</P1>
            </div>
          </MenuItem>
        ) : null}
        {slides?.withoutAnnotations ? (
          <MenuItem ref={ref}>
            <div
              onClick={openWithout}
              data-href={slides?.withoutAnnotations}
              className="slides"
            >
              <P1 color="var(--color-text-primary)">Without annotation</P1>
            </div>
          </MenuItem>
        ) : null}

        {isBatch && !isBatchGroup && (
          <MenuItem ref={ref}>
            <div onClick={goToCoursePage}>
              <P1 color="var(--color-text-primary)">Go to course page</P1>
            </div>
          </MenuItem>
        )}
      </>
    );
  }
);

PDFDownload.displayName = 'PDFDownload';

PDFDownload.propTypes = {
  selectedLesson: PropTypes.objectOf(PropTypes.any),
  handleClose: PropTypes.func.isRequired,
  isBatch: PropTypes.bool,
  isBatchGroup: PropTypes.bool,
  me: PropTypes.objectOf(PropTypes.any),
  source: PropTypes.string,
  course: PropTypes.objectOf(PropTypes.any),
  goal: PropTypes.objectOf(PropTypes.any)
};

PDFDownload.defaultProps = {
  selectedLesson: {},
  isBatch: false,
  isBatchGroup: false,
  me: null,
  source: null,
  course: {},
  goal: {}
};

export default PDFDownload;
